import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AuthenticationNavigation from '../../components/navigation/AuthenticationNavigation';
import RegistrationForm from './components/RegistrationForm';
import SecurityFeatures from './components/SecurityFeatures';
import BenefitsSection from './components/BenefitsSection';
import Icon from '../../components/AppIcon';

const Register = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Register - CyberOddEven | Create Your Account</title>
        <meta 
          name="description" 
          content="Join CyberOddEven and start your psychological gaming journey. Create an account to battle AI opponents, earn gems, and unlock exclusive content in this cyberpunk-themed odd-even game." 
        />
      </Helmet>

      <AuthenticationNavigation />

      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/20 rounded-sm flex items-center justify-center border-2 border-primary shadow-glow-cyan-md">
                    <Icon name="UserPlus" size={28} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold neon-text-cyan">
                      Create Account
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground font-caption mt-1">
                      Join the psychological gaming revolution
                    </p>
                  </div>
                </div>

                <div className="p-4 md:p-6 bg-card rounded-sm border-2 border-primary/30 shadow-glow-cyan-sm">
                  <div className="flex items-start gap-3 md:gap-4">
                    <Icon name="Sparkles" size={24} color="var(--color-accent)" className="flex-shrink-0 mt-1" />
                    <div>
                      <h2 className="text-base md:text-lg font-caption font-semibold text-accent mb-2">
                        Welcome to CyberOddEven
                      </h2>
                      <p className="text-sm md:text-base text-foreground leading-relaxed">
                        Experience the ultimate psychological battle in a cyberpunk-themed odd-even game. Face AI opponents with distinct personalities, earn rare gems, and unlock exclusive content as you progress through increasingly challenging gameplay.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                <BenefitsSection />
              </div>
            </div>

            <div className="space-y-6 md:space-y-8">
              <div className="p-6 md:p-8 bg-card rounded-sm border-2 border-primary shadow-glow-cyan-md">
                <div className="mb-6 md:mb-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-primary mb-2">
                    Registration Form
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground font-caption">
                    Fill in your details to create your gaming account
                  </p>
                </div>

                <RegistrationForm />

                <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-primary/30">
                  <p className="text-sm md:text-base text-center text-muted-foreground font-caption">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className="text-primary hover:text-secondary transition-colors duration-base font-semibold underline"
                    >
                      Login here
                    </Link>
                  </p>
                </div>
              </div>

              <div className="hidden md:block">
                <SecurityFeatures />
              </div>

              <div className="block lg:hidden">
                <BenefitsSection />
              </div>

              <div className="block md:hidden">
                <SecurityFeatures />
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-12 lg:mt-16">
            <div className="p-4 md:p-6 bg-card rounded-sm border border-primary/30">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center border border-primary">
                  <Icon name="ShieldCheck" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-caption font-semibold text-primary mb-2">
                    Your Data is Protected
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    We use industry-standard encryption and security measures to protect your personal information. Your privacy and security are our top priorities. We comply with all relevant data protection regulations and never share your information without your consent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;